import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaUsers, FaUser } from "react-icons/fa6";
import {
  MdOutlineStackedLineChart,
  MdCheckCircleOutline,
} from "react-icons/md";
import { HiCollection } from "react-icons/hi";
import Swal from "sweetalert2";
import axios from "axios";
import { BsGrid1X2 } from "react-icons/bs";
import { AiOutlineDollar } from "react-icons/ai";

export default function AgentSidebar() {
  const navigate = useNavigate();
  const [isSidebarVisible, setSidebarVisible] = useState(false);
  const [themeColor, setThemeColor] = useState(
    "radial-gradient(circle at 12.3% 19.3%, rgb(85, 88, 218) 0%, rgb(95, 209, 249) 100.2%)"
  );
  const baseURL = process.env.REACT_APP_BASE_URL;

  const fetchThemeColor = async () => {
    try {
      const accessToken = sessionStorage.getItem("access_token");
      const response = await axios.get(`${baseURL}/fetchSetup`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = response.data;
      if (data && data.length > 0) {
        setThemeColor(data[0].theme_color || "#000000");
      }
    } catch (error) {
      console.error("Error fetching theme color:", error);
    }
  };

  useEffect(() => {
    fetchThemeColor();
  }, []);

  const handleLogoutConfirmation = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will be logged out!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout!",
    }).then((result) => {
      if (result.isConfirmed) {
        navigate("/Logout");
      }
    });
  };

  const toggleSidebar = () => {
    setSidebarVisible((prev) => !prev);
  };

  return (
    <>
      {/* Hamburger menu for mobile */}
      <button
        onClick={toggleSidebar}
        className="inline-flex items-center p-2 mt-2 ms-3 text-sm text-amber-400 rounded-lg sm:hidden hover:bg-blue-900"
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 5.25a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10zm0 5.25a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75a.75.75 0 01-.75-.75z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 w-64 h-screen transform transition-transform duration-300 ${
          isSidebarVisible ? "translate-x-0" : "-translate-x-full"
        } sm:translate-x-0 bg-blue-700`}
        style={{ backgroundColor: themeColor }}
      >
        <div className="h-full px-3 py-4 overflow-y-auto">
          <ul className="space-y-2 font-medium text-white">
            {/* Dashboard */}
            <li>
              <Link
                to="/AgentDashboard"
                onClick={() => setSidebarVisible(false)}
                className="flex items-center p-2 rounded-lg hover:bg-blue-900"
              >
                <BsGrid1X2 className="w-6 h-6 text-amber-400" />
                <span className="ms-3">Dashboard</span>
              </Link>
            </li>

            {/* Customers */}
            <li>
              <Link
                to="/Customers"
                onClick={() => setSidebarVisible(false)}
                className="flex items-center p-2 rounded-lg hover:bg-blue-900"
              >
                <FaUsers className="w-6 h-6 text-amber-400" />
                <span className="ms-3">Customers</span>
              </Link>
            </li>

            {/* Loan */}
            <li>
              <Link
                to="/Loan"
                onClick={() => setSidebarVisible(false)}
                className="flex items-center p-2 rounded-lg hover:bg-blue-900"
              >
                <MdOutlineStackedLineChart className="w-6 h-6 text-amber-400" />
                <span className="ms-3">Loan</span>
              </Link>
            </li>

            {/* Loan Collection */}
            <li>
              <Link
                to="/LoanCollection"
                onClick={() => setSidebarVisible(false)}
                className="flex items-center p-2 rounded-lg hover:bg-blue-900"
              >
                <HiCollection className="w-6 h-6 text-amber-400" />
                <span className="ms-3">Loan Collection</span>
              </Link>
            </li>
            <li>
              <Link
                to="/myprofile"
                onClick={() => setSidebarVisible(false)}
                className="flex items-center p-2 rounded-lg hover:bg-blue-900"
              >
                <FaUser className="w-6 h-6 text-amber-400" />
                <span className="ms-3">My Profile</span>
              </Link>
            </li>
            <li>
              <Link
                to="/Attendance"
                onClick={() => setSidebarVisible(false)}
                className="flex items-center p-2 rounded-lg hover:bg-blue-900"
              >
                <MdCheckCircleOutline className="w-6 h-6 text-amber-400" />
                <span className="ms-3">Attendance</span>
              </Link>
            </li>

            <li>
              <Link
                to="/AgentAttendanceReport"
                onClick={() => setSidebarVisible(false)}
                className="flex items-center p-2 rounded-lg hover:bg-blue-900"
              >
                <MdOutlineStackedLineChart className="w-6 h-6 text-amber-400" />
                <span className="ms-3">Attendance Report</span>
              </Link>
            </li>

            <li>
              <Link
                to="/AgentAdvanceReport"
                onClick={() => setSidebarVisible(false)}
                className="flex items-center p-2 rounded-lg hover:bg-blue-900"
              >
                <MdOutlineStackedLineChart className="w-6 h-6 text-amber-400" />
                <span className="ms-3">Advance Report</span>
              </Link>
            </li>

            <li>
              <Link
                data-drawer-hide="sidebar-multi-level-sidebar"
                onClick={() => setSidebarVisible(false)}
                to="/Advance"
              >
                <div className="flex items-center p-2 text-slate-100 rounded-lg dark:text-white hover:bg-blue-900 dark:hover:bg-gray-700 group">
                  <AiOutlineDollar className="flex-shrink-0 w-6 h-6 text-amber-400 transition duration-75 dark:text-gray-400 group-hover:text-slate-100 dark:group-hover:text-white" />

                  <span className="ms-3 md:hidden  lg:block">Advance</span>
                </div>
              </Link>
            </li>

            {/* Logout */}
            <li>
              <div
                onClick={handleLogoutConfirmation}
                className="flex items-center p-2 rounded-lg hover:bg-blue-900 cursor-pointer"
              >
                <svg
                  className="w-6 h-6 text-amber-400"
                  fill="none"
                  viewBox="0 0 18 16"
                >
                  <path
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M1 8h11m0 0L8 4m4 4-4 4m4-11h3a2 2 0 012 2v10a2 2 0 01-2 2h-3"
                  />
                </svg>
                <span className="ms-3">Logout</span>
              </div>
            </li>
          </ul>
        </div>
      </aside>
    </>
  );
}
